'use client';

import React from 'react';
import NextLink from 'next/link';
import classnames from 'classnames';
import { ConLinkAtom } from '@gandalf/babylon/atoms/link';
import { LinkProps } from './Link.types';
import { LinkProps as NextLinkProps } from 'next/link';
import { attachQueryParams } from '@frodo/lib/utils/common/urlUtils';
import { useVct } from '@frodo/lib/hooks';

const buildHref = (
  href: NextLinkProps['href'],
  vct: string,
): NextLinkProps['href'] => {
  let reBuiltHref: NextLinkProps['href'];
  if (typeof href === 'string') {
    reBuiltHref = attachQueryParams(href, { vct });
  } else if (typeof href?.query === 'string') {
    const query = attachQueryParams(href?.query as string, { vct });
    reBuiltHref = {
      ...href,
      query,
    };
  } else if (
    Object.prototype.toString.call(href?.query) === '[object Object]'
  ) {
    reBuiltHref = {
      ...href,
      query: {
        ...href?.query,
        vct,
      },
    };
  } else {
    reBuiltHref = {
      ...href,
      query: {
        vct,
      },
    };
  }

  return reBuiltHref;
};

export const Link = ({
  color,
  responsiveFontSizes,
  disabled = false,
  className,
  href,
  rel,
  vct,
  children,
  target = '_self',
  qa,
  title,
  useNextLink = false,
  ariaLabel,
  onClick,
  fontWeight,
  tabIndex,
  ...linkProps
}: LinkProps) => {
  const { buildVct } = useVct();
  let reBuiltHref = href;

  if (vct) {
    reBuiltHref = buildHref(href, buildVct(vct));
  }

  if (!useNextLink) {
    return (
      <ConLinkAtom
        color={color}
        disabled={disabled}
        ariaLabel={ariaLabel}
        responsiveFontSizes={responsiveFontSizes}
        className={className}
        rel={rel}
        href={reBuiltHref.toString()}
        qa={qa}
        title={title}
        target={target}
        onClick={onClick}
        fontWeight={fontWeight}
        tabIndex={tabIndex}
      >
        {children}
      </ConLinkAtom>
    );
  }

  return (
    <ConLinkAtom
      color={color}
      disabled={disabled}
      ariaLabel={ariaLabel}
      responsiveFontSizes={responsiveFontSizes}
      className={className}
      fontWeight={fontWeight}
      renderComponent={(linkAtomClasses: string) => (
        <NextLink
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          rel={rel}
          onClick={onClick}
          {...linkProps}
          prefetch={false}
          href={reBuiltHref.toString()}
          data-qa={qa}
          title={title}
          className={classnames(linkAtomClasses)}
          target={target}
        >
          {children}
        </NextLink>
      )}
    />
  );
};
